/**
 * Template Selectors
 *
 * Memoized selectors for accessing chat template state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { FlightsReviewTemplateData } from '@contracts';

// ============================================
// Base Selectors
// ============================================

const selectTemplateState = (state: RootState) => state.chat.template;

// ============================================
// Template Selectors (Memoized)
// ============================================

export const selectActiveTemplate = createSelector(
  [selectTemplateState],
  (template) => template.activeTemplate,
);

export const selectTemplateData = createSelector(
  [selectTemplateState],
  (template) => template.templateData,
);

/**
 * Select pending template - indicates which template is being loaded
 * Used to show skeleton loaders during template transitions
 */
export const selectPendingTemplate = createSelector(
  [selectTemplateState],
  (template) => template.pendingTemplate,
);

/**
 * Select tracking info from the current template response
 */
export const selectTrackingInfo = createSelector(
  [selectTemplateState],
  (template) => template.trackingInfo,
);

/**
 * Select contextHashKey from flights-review template data
 */
export const selectContextHashKey = createSelector([selectTemplateData], (templateData) => {
  if (templateData && 'contextHashKey' in templateData) {
    return (templateData as FlightsReviewTemplateData).contextHashKey;
  }
  return undefined;
});
